// import { UserService } from "../users/user.service";
import { v4 as uuidv4 } from "uuid"; // npm install uuid @types/uuid
import toast from "react-hot-toast";

// Tipos de mensaje definidos por el desarrollador backend
export type MessageType = "status" | "chunk" | "heartbeat" | "complete" | "chart" | "csv_url";

export interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	content: string;
	type: MessageType | "text"; // 'text' para el usuario
	timestamp: number;
	fileUrl?: string;
	chartData?: any;
}

export class ChatService {
	// private userService: UserService;
	private ws: WebSocket | null = null;
	private _messages: ChatMessage[] = [];
	private _isTyping: boolean = false;
	private _sessionId: string;
	private userData = JSON.parse(sessionStorage.getItem("userData") || "null");
	private _inactivityTimerId?: number;
	private _messagesBackup: ChatMessage[] | null = null;

	// Callbacks para la reactividad en React
	private listeners: Array<(messages: ChatMessage[]) => void> = [];

	constructor() {
		// this.userService = userService;
		// 1. Gestionar Session ID (36 caracteres uuidv4)
		this._sessionId = this.initSession();
		// 2. Cargar historial previo de esta sesión
		this.loadFromStorage();
	}

	private initSession(): string {
		let sid = sessionStorage.getItem("chat_session_id");
		if (!sid) {
			sid = uuidv4();
			sessionStorage.setItem("chat_session_id", sid);
		}
		return sid;
	}

	// --- SUSCRIPCIÓN PARA COMPONENTES REACT ---
	public subscribe(callback: (messages: ChatMessage[]) => void) {
		this.listeners.push(callback);
		callback([...this._messages]);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== callback);
		};
	}

	private notify() {
		this.listeners.forEach((l) => l([...this._messages]));
		this.saveToStorage();
	}

	// --- ENVIAR MENSAJE ---
	public sendMessage(messageText: string) {
		if (!messageText.trim()) return;

		// Snapshot current UI state so we can revert on timeout
		this._messagesBackup = this._messages.slice();

		// Agregar mensaje del usuario localmente
		const userMsg: ChatMessage = {
			id: uuidv4(),
			role: "user",
			content: messageText,
			type: "text",
			timestamp: Date.now(),
		};

		// Crear placeholder para la respuesta del asistente
		const assistantMsg: ChatMessage = {
			id: uuidv4(),
			role: "assistant",
			content: "",
			type: "status", // Empieza como status (según logs: "Processing...")
			timestamp: Date.now(),
		};

		this._messages = [...this._messages, userMsg, assistantMsg];
		this._isTyping = true;
		this.notify();

		this.connectAndStream(messageText, assistantMsg.id);
	}

	private connectAndStream(text: string, assistantMsgId: string) {
		const URL: string = process.env.REACT_APP_ASSISTANT!;
		this.ws = new WebSocket(URL);

		this.ws.onopen = () => {
			// Payload exacto solicitado por el desarrollador
			const payload = {
				action: "sendMessage",
				message: text,
				session_id: this._sessionId,
				user_email: this.userData.email,
				organization: this.userData.organization_name,
				chat_history: this.prepareHistoryForBackend(),
			};
			this.ws?.send(JSON.stringify(payload));
			// Start inactivity watchdog
			this.resetInactivityTimer();
		};

		this.ws.onmessage = (event) => {
			const response = JSON.parse(event.data);
			console.log("📥 Mensaje recibido de WebSocket:", response);
			this.handleIncomingChunk(response, assistantMsgId);
		};

		this.ws.onclose = () => {
			this._isTyping = false;
			this.notify();
		};

		this.ws.onerror = (err) => {
			console.error("WebSocket Error:", err);
			this._isTyping = false;
			this.notify();
		};
	}

	private handleIncomingChunk(data: any, msgId: string) {
		// Any incoming data resets the inactivity timer
		this.resetInactivityTimer();

		const index = this._messages.findIndex((m) => m.id === msgId);
		if (index === -1) return;

		const updatedMsg = { ...this._messages[index] };

		switch (data.type) {
			case "status":
				if (updatedMsg.content.length > 0) {
					updatedMsg.content += "\n" + data.data;
				} else {
					updatedMsg.content = data.data;
				}
				updatedMsg.type = "status";
				break;

			case "chunk":
				updatedMsg.type = "chunk";
				updatedMsg.content += data.data;
				break;

			case "csv_url":
				updatedMsg.fileUrl = data.data; // Guardamos la URL recibida directamente
				break;

			case "chart":
				// 1. Cambiamos el tipo a chart para que el componente React sepa qué renderizar
				updatedMsg.type = "chart";
				// 2. Guardamos la configuración del gráfico
				updatedMsg.chartData = data.data;

				/** * 3. EXPLICACIÓN DEL CAMBIO:
				 * En lugar de borrar todo con: updatedMsg.content = data.data.caption;
				 * Verificamos si ya tenemos contenido previo acumulado de los 'chunks'.
				 * Si el caption del gráfico es diferente al texto ya recibido, lo añadimos.
				 **/
				const newCaption = data.data.caption || "";
				if (!updatedMsg.content.includes(newCaption)) {
					updatedMsg.content = updatedMsg.content + "\n\n" + newCaption;
				}
				break;

			case "heartbeat":
				return;

			case "complete":
				// Mantenemos la lógica de no sobreescribir si ya es un gráfico
				if (updatedMsg.type !== "chart") {
					updatedMsg.type = "chunk";
				}

				// Si el 'complete' trae un campo 'response' de texto (como se ve en tus logs)
				// podrías validarlo aquí para asegurar que no falte nada de texto final.
				if (data.data?.response && !updatedMsg.content.includes(data.data.response)) {
					// Opcional: updatedMsg.content = data.data.response;
					// (Usa esto solo si el 'response' final es el resumen completo de todo el chat)
				}

				this._isTyping = false;
				this.ws?.close();
				this.clearInactivityTimer();
				break;
		}

		this._messages[index] = updatedMsg;
		this.notify();
	}

	// --- HELPERS ---
	private prepareHistoryForBackend() {
		// El backend espera: { role: "user" | "assistant", content: string }
		// Filtramos el mensaje actual que se está escribiendo y los status
		return this._messages
			.slice(0, -2) // No enviar el mensaje que acabamos de agregar
			.filter((m) => m.type !== "status")
			.map((m) => ({
				role: m.role,
				content: m.content,
			}));
	}

	private saveToStorage() {
		sessionStorage.setItem(`chat_history_${this._sessionId}`, JSON.stringify(this._messages));
	}

	private loadFromStorage() {
		const saved = sessionStorage.getItem(`chat_history_${this._sessionId}`);
		if (saved) {
			this._messages = JSON.parse(saved);
		}
	}

	// Inside NasChatService class
	public clearChat() {
		// 1. Reset local array
		this._messages = [];

		// 2. Clear from storage
		sessionStorage.removeItem(`chat_history_${this._sessionId}`);

		// 3. Notify listeners (React components) so the UI updates
		this.notify();
	}

	public get isTyping() {
		return this._isTyping;
	}

	//Timer functions
	private resetInactivityTimer() {
		this.clearInactivityTimer();
		this._inactivityTimerId = window.setTimeout(
			() => this.handleInactivityTimeout(),
			15000, // 15 seconds
		) as unknown as number;
	}

	private clearInactivityTimer() {
		if (this._inactivityTimerId != null) {
			window.clearTimeout(this._inactivityTimerId);
			this._inactivityTimerId = undefined;
		}
	}

	private handleInactivityTimeout() {
		// Show error toast
		toast.error("Error: Tiempo de respuesta agotado. Inténtelo de nuevo.");

		// Revert UI to the state before the last assistant reply started
		if (this._messagesBackup) {
			this._messages = this._messagesBackup;
			this._messagesBackup = null;
			this.notify();
		}

		// Clean up
		this._isTyping = false;
		this.ws?.close();
		this.clearInactivityTimer();
	}
}
